import { AlmconnectionRallyApiClient } from "@/natsstubs/almconnection/rally/client";
import type { SetRallyProjectMappingRequest } from "@/natsstubs/almconnection/rally/models";
import { keysToSnake } from "@/natsstubs/wire/transform";

import type { NatsRpcBackend } from "../NatsRpcBackend";
import type { UnknownRecord } from "../RpcBackend";
import { errMessage } from "./errMessage";

export function registerRallyAdapters(nats: NatsRpcBackend): void {
  const client = new AlmconnectionRallyApiClient(nats.caller);
  const cid = (args: unknown[], kwargs?: UnknownRecord): string =>
    String(args[0] ?? kwargs?.connection_id);

  nats.register("alm.ac", "alm-tools", {
    get_project_mapping: async (args: unknown[]) =>
      keysToSnake((await client.getRallyProjectMapping(cid(args))).mapping),

    set_project_mapping: async (args: unknown[]) =>
      client.setRallyProjectMapping(
        { mapping: args[1] as SetRallyProjectMappingRequest["mapping"] },
        cid(args),
      ),

    get_projects: async (args: unknown[]) => keysToSnake(await client.getRallyProjects(cid(args))),

    get_tags: async (args: unknown[]) => ({
      tags: (await client.getRallyTags(cid(args))).tags.map((t) => ({
        tag_id: t.objectId,
        name: t.name,
      })),
    }),

    get_issue_types: async (args: unknown[], kwargs?: UnknownRecord) => ({
      ac_types: keysToSnake((await client.getRallyIssueTypes(cid(args, kwargs))).issueTypes),
    }),

    get_portfolio_workitem_types: async (args: unknown[], kwargs?: UnknownRecord) =>
      keysToSnake((await client.getRallyPortfolioWorkitemTypes(cid(args, kwargs))).workitemTypes),

    get_releases: async (args: unknown[], kwargs?: UnknownRecord) =>
      (
        await client.getRallyReleases(
          {
            projectId: (kwargs?.project_id as number | null) ?? null,
            artIds: (kwargs?.art_ids as number[] | null) ?? null,
          },
          cid(args),
        )
      ).releases.map((r) => ({ ObjectID: r.objectId, Name: r.name, State: r.state })),

    get_iterations: async (args: unknown[]) =>
      (await client.getRallyIterations({ projectId: Number(args[1]) }, cid(args))).iterations.map(
        (i) => ({ ObjectID: i.objectId, Name: i.name, State: i.state }),
      ),

    get_fields: async (args: unknown[]) => {
      try {
        const r = await client.getRallyFields(
          { acType: args[1] as string, fieldType: args[2] as "OBJECT" | "DECIMAL" },
          cid(args),
        );
        return {
          success: true,
          fields: r.fields.map((f) => ({ ElementName: f.elementName, Name: f.name })),
        };
      } catch (e) {
        return { success: false, error: errMessage(e) };
      }
    },

    create_test_item: async (args: unknown[]) => {
      try {
        const r = await client.createRallyTestItem(
          {
            projectId: Number(args[1]),
            workitemType: String(args[2]),
            defaultFields: typeof args[3] === "string" ? args[3] : "",
          },
          cid(args),
        );
        return { success: true, formatted_id: r.formattedId, url: r.url };
      } catch (e) {
        return { success: false, error: errMessage(e) };
      }
    },

    get_webhooks: async (args: unknown[]) => {
      try {
        const r = await client.getRallyWebhooks(cid(args));
        return {
          success: true,
          webhooks: r.webhooks.map((wh) => ({
            Name: wh.name,
            _ref: wh.webhookRef,
            ObjectUUID: wh.objectUuid,
            TargetUrl: wh.targetUrl,
          })),
        };
      } catch (e) {
        return { success: false, error: errMessage(e) };
      }
    },

    create_webhook: async (args: unknown[], kwargs?: UnknownRecord) => {
      try {
        await client.createRallyWebhook({ name: kwargs?.name as string }, cid(args, kwargs));
        return { success: true };
      } catch (e) {
        return { success: false, error: errMessage(e) };
      }
    },

    delete_webhook: async (args: unknown[], kwargs?: UnknownRecord) => {
      try {
        await client.deleteRallyWebhook(
          { webhookRef: kwargs?.webhook_ref as string },
          cid(args, kwargs),
        );
        return { success: true };
      } catch (e) {
        return { success: false, error: errMessage(e) };
      }
    },

    get_workspaces: async (args: unknown[], kwargs?: UnknownRecord) => {
      try {
        const r = await client.getRallyTenants(cid(args, kwargs));
        return { success: true, workspaces: keysToSnake(r.tenants) };
      } catch (e) {
        return { success: false, error: errMessage(e) };
      }
    },

    get_workspace: async (args: unknown[], kwargs?: UnknownRecord) =>
      (await client.getRallyTenantId(cid(args, kwargs))).tenantId,

    set_workspace: async (args: unknown[]) =>
      client.setRallyTenantId({ tenantId: String(args[1]) }, cid(args)),
  });
}
